import React, { useMemo, useState } from 'react'
import { getGroupedKeyParamFromLayout, TgroupKeyParam } from '../../shared/utils/comparaison'
import { Alert, Autocomplete, Avatar, Box, Button, Card, CardContent, Checkbox, Divider, Grid, IconButton, LinearProgress, Stack, TextField, Tooltip, Typography } from '@mui/material'
import { TtrainingKeyParameter } from '../../shared/types/trainingKeyParameter.type'
import { TtrainingKeyParameterItem } from '../../shared/types/TrainingKeyParameterItem.type'
import { AccessTime } from '@mui/icons-material'
import { formatDate, getCorrectDateFormat } from '../../shared/utils/DateUtils'
import { useSelector } from 'react-redux'
import { RootState } from '../../store'
import { Link, useNavigate } from 'react-router-dom'
import { routes } from '../../pages/routes'
import { capitalize, flatten, isEmpty, maxBy, sortBy, upperCase } from 'lodash'
import { useMyTrainings } from '../../hooks/useMyTrainings'
import { useExam } from '../../hooks/useExam'
import { Icon } from '@iconify/react'
import { TkeyParamLayout } from '../../shared/types/computedKeyParam.type'
import { useDispatch } from 'react-redux'
import { setCategories } from '../../store/reducers/comparaison.reducer'
import { t } from 'i18next'

function ComparaisonCard({ categorie, data, onSelectExam, selectedExams, canChangeParam, showExplenation = true, printable }: { printable?: boolean, canChangeParam?: boolean, selectedExams?: string[], categorie: TgroupKeyParam, data?: TtrainingKeyParameter, onSelectExam?: (exam: string, checked: boolean) => void, showExplenation?: boolean }) {
    const allCategories = useSelector<RootState>(state => state.comparaison.allCategories) as TkeyParamLayout[]
    const categories = useSelector<RootState>(state => state.comparaison.categories) as TgroupKeyParam[]
    const dispatch = useDispatch()
    const [showSelect, setShowSelect] = useState(false)



    const sortedCategories = useMemo(() => {
        if (isEmpty(allCategories)) return []

        let existing = categories.map(c => c.key)

        return sortBy(allCategories.filter(c => !existing.includes(c.insight_parameter.name)), categorie => categorie?.insight_parameter?.name_translated)
    }, [allCategories, categories])

    const onNewParamSelected = (param: TkeyParamLayout | null) => {
        if (param) {
            let index = categories.findIndex(c => c.key === categorie.key)

            let formatParamToGroupFormat: TgroupKeyParam = {
                key: param.insight_parameter?.name,
                title: param.insight_parameter?.name_translated,
                detail: param.insight_parameter?.details_translated,
                id: param.insight_parameter['@id']
            }

            let newCategories = [...categories]

            newCategories[index] = formatParamToGroupFormat

            dispatch(setCategories(newCategories))

        }
    }

    const breakProperty = useMemo(() => {
        return (data?.items && data.items.length < 10) ? 'avoid' : 'auto'
    }, [data?.items.length])


    return (
        <Card sx={{ display: 'block', pageBreakInside: breakProperty, breakInside: breakProperty, boxShadow: printable ? 'none' : 'initial' }} >
            <CardContent sx={{ p: printable ? '2px !important' : '24px' }}>
                <Box alignItems={'center'} display={'flex'} gap={0.5} mb={1}>
                    {
                        !showSelect ?
                            <Typography sx={{ fontSize: 14 }} fontWeight={500} color="#808080" display={'flex'} alignItems={'center'} gap={0.5} >

                                {showExplenation && <Tooltip arrow placement='top' title={<Typography
                                    variant="caption"
                                    component="div"
                                    sx={{ whiteSpace: "pre-line" }}
                                    dangerouslySetInnerHTML={{ __html: categorie?.detail ?? "" }}
                                />}>
                                    <Icon icon='garden:info-stroke-16' />
                                </Tooltip>}

                                {categorie.title}
                            </Typography> :
                            <Autocomplete
                                options={sortedCategories}
                                getOptionKey={option => option['@id']}
                                isOptionEqualToValue={(option, value) => option['@id'] === value['@id']}
                                sx={{ width: '300px' }}
                                size='small'
                                disableClearable
                                value={allCategories.find(c => c.insight_parameter.name === categorie.key)}
                                onChange={(_, value) => onNewParamSelected(value)}
                                getOptionLabel={option => option.insight_parameter.name_translated}
                                renderInput={(params) => <TextField {...params} label={t('settings:availableParameters')} />}
                            />
                    }
                    {
                        canChangeParam && <Tooltip title={t('horse:changeParam')} arrow placement='right'>
                            <IconButton onClick={() => setShowSelect(!showSelect)} size='small'>
                                <Icon icon='clarity:switch-line'></Icon>
                            </IconButton>
                        </Tooltip>
                    }

                </Box>
                <Stack direction={'column'} spacing={!printable ? 1 : 0} >
                    {(data?.items && data.items.length > 0) && data.items.map((item, i) => (
                        <Box key={item['@id'] || `item-${item.training?.id}`}>
                            <RenderInfos
                                onSelectExam={onSelectExam}
                                selectedExams={selectedExams}
                                items={data.items}
                                item={item}
                                categorie={categorie.key}
                                printable={printable}
                            />
                            {i !== data.items.length - 1 && !printable && <Divider />}
                        </Box>
                    ))}
                </Stack>

            </CardContent>
        </Card>
    )
}

const RenderInfos = ({ item, categorie, items, onSelectExam, selectedExams, printable }: { printable?: boolean, selectedExams?: string[], onSelectExam?: (exam: string, checked: boolean) => void, item: TtrainingKeyParameterItem, categorie?: string, items: TtrainingKeyParameterItem[] }) => {
    const dateFormat = useSelector<RootState>(state => state.user.user?.default_date_format) as string | undefined
    const navigate = useNavigate()
    const { renderTrainingTypeAuto } = useExam(false)

    const getDateFormat = (): string => {
        if (dateFormat) {
            return getCorrectDateFormat(dateFormat) + ' HH:mm'
        } else {
            return 'DD/MM/YYYY HH:mm'
        }
    }



    let keyParamItem = useMemo(() => {
        return item.computed_key_parameters.find(e => e.key_parameter_layout?.insight_parameter?.name === categorie)
    }, [categorie, item])

    let filtered = flatten(items.map(item => item.computed_key_parameters.filter(e => e.key_parameter_layout.insight_parameter.name === categorie)))



    const maxValue = useMemo(() => {
        return maxBy(filtered, (e) => e.raw_numeric_value)
    }, [filtered])



    const getPercentage = () => {
        if (!keyParamItem || !maxValue) return 0;

        const value = Number(keyParamItem.raw_numeric_value);
        const max = Number(maxValue.raw_numeric_value);

        if (!Number.isFinite(value) || !Number.isFinite(max) || max <= 0) {
            return 0;
        }

        return (value * 100) / max;
    };

    return (
        <Grid container sx={{ pageBreakInside: 'avoid', breakInside: 'avoid' }} alignItems={'center'} >

            {/* Horse and training section */}
            <Grid item md={!printable ? 6 : 8} xs={printable ? 8 : 12}>

                <Grid container spacing={1} alignItems={'center'}>
                    <Grid item md={5} xs={5}>
                        <Box display={'flex'} alignItems={'center'}>
                            {typeof onSelectExam === 'function' && <Checkbox checked={selectedExams && selectedExams.includes(item.training['@id'])} onChange={(event, checked) => onSelectExam(item.training['@id'], checked)} />}
                            <Typography component={Link} color={'inherit'} to={routes.DASHBOARD_ROUTES.EXAM_VIEW.replace(':id', String(item.training.id))} sx={{ cursor: 'pointer' }} target='_blank' variant='caption' fontWeight={500}>{upperCase(item?.training?.horse_trainer?.horse?.name ?? " - ")}</Typography>
                        </Box>
                    </Grid>
                    <Grid item md={4} xs={4}>
                        {
                            item?.training?.creation_date && <Typography variant='caption'>{formatDate(item.training.creation_date, getDateFormat())}</Typography>
                        }
                    </Grid>
                    <Grid item md={3} xs={3}>
                        <Typography variant='caption' >
                            <span dangerouslySetInnerHTML={{ __html: renderTrainingTypeAuto(item.training) ?? "" }}></span>
                        </Typography>
                    </Grid>



                </Grid>
            </Grid>


            {/* Bar Section */}
            <Grid item md={!printable ? 6 : 4} xs={printable ? 4 : 12}>
                {/* Progress Section */}
                <Box display={'flex'} flexDirection={'row'} flex={1} alignItems={'center'} gap={3}>
                    <LinearProgress variant="determinate" value={getPercentage()} sx={{
                        width: '100%',
                        flexGrow: 1,
                        borderRadius: 3,
                        height: 8.5,
                        ...(printable && {
                            backgroundColor: 'transparent',
                            border: '0.5px solid #808080',
                        })
                    }} />
                    <Typography variant='caption' flexBasis={'100px'}>{keyParamItem?.formatted_value}</Typography>
                </Box>
            </Grid>


        </Grid>
    )
}

export default ComparaisonCard